/**
 * The screen's own actions — Export and the form-level custom buttons — drawn
 * the SAME way on every list layout (List, Spread sheet, Bulk entry,
 * Compact): an item placed `'bar'` is a button on the toolbar, every other
 * one (the default, `actionPlacement`) sits in a ⋮ menu so a screen does not
 * fill up with buttons people rarely use.
 *
 * TxnScreen builds the items once; ListView folds the menu items into its own
 * ⋮, the other layouts draw <ScreenActions> (buttons + a ⋮ of its own).
 */
import type { CSSProperties, ReactNode } from 'react'
import type { MoreMenuItem } from '../../customize/ui'
import { tw } from '../../testHooks'
import RowActionsMenu from './RowActionsMenu'

export interface ScreenActionItem {
  key: string
  label: string
  icon?: ReactNode
  placement: 'bar' | 'menu'
  onClick: () => void
  /** The test hook (`data-tw-action`): the model action's name, or a verb. */
  hook: string
  /** `form` for a custom button (`data-tw-scope`). */
  scope?: string
  /** The keyboard shortcut that runs it, as shown ("Alt+A"). */
  shortcut?: string
}

const barBtn: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px',
  borderRadius: 8, border: '1px solid var(--color-border-strong, #c7cfd9)',
  background: 'var(--color-bg-secondary, #ffffff)', color: 'var(--color-text, #1c2430)',
  fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit',
}

export function barItems(items: ScreenActionItem[]): ScreenActionItem[] {
  return items.filter(i => i.placement === 'bar')
}

export function menuItems(items: ScreenActionItem[]): ScreenActionItem[] {
  return items.filter(i => i.placement !== 'bar')
}

/** The menu-placed items as entries of a host's ⋮ (the list's own menu),
 *  under an "Actions" heading. */
export function asMoreMenuItems(items: ScreenActionItem[]): MoreMenuItem[] {
  return menuItems(items).map((i, n) => ({
    label: i.label, icon: i.icon, onClick: i.onClick, hook: i.hook, shortcut: i.shortcut,
    ...(n === 0 ? { heading: 'Actions' } : {}),
  }))
}

/** The bar-placed items as toolbar buttons. */
export function ScreenActionButtons({ items, busy }: { items: ScreenActionItem[]; busy?: boolean }) {
  const bar = barItems(items)
  if (!bar.length) return null
  return (
    <>
      {bar.map(i => (
        <button key={`sa-${i.key}`} type="button" onClick={i.onClick} disabled={busy}
          title={i.shortcut ? `${i.label} (${i.shortcut})` : i.label}
          {...tw('action', i.hook, i.scope ? { scope: i.scope } : undefined)} style={barBtn}>
          {i.icon}{i.label}
        </button>
      ))}
    </>
  )
}

/** Buttons for the bar items + a ⋮ of its own for the rest — for a layout
 *  whose toolbar has no menu to fold them into. */
export function ScreenActions({ items, busy }: { items: ScreenActionItem[]; busy?: boolean }) {
  const menu = menuItems(items)
  if (!items.length) return null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <ScreenActionButtons items={items} busy={busy} />
      {menu.length > 0 && (
        <span title="More actions" {...tw('action', 'screen-actions-menu')}>
          <RowActionsMenu circled actions={menu.map(i => ({
            label: i.label, icon: i.icon, onClick: i.onClick, hook: i.hook, shortcut: i.shortcut,
          }))} />
        </span>
      )}
    </span>
  )
}
